import * as React from 'react';
import { cn } from '@/lib/utils';

interface TabsContextValue {
  activeTab: string;
  onTabChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const context = React.useContext(TabsContext);
  if (!context) throw new Error('Tabs components must be used within <Tabs>');
  return context;
}

/* ─── Root ─── */

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  onValueChange: (value: string) => void;
}

/** Tab container — manages active tab state */
function Tabs({ value, onValueChange, className, children, ...props }: TabsProps): React.JSX.Element {
  const ctx = React.useMemo(() => ({ activeTab: value, onTabChange: onValueChange }), [value, onValueChange]);
  return (
    <TabsContext value={ctx}>
      <div className={cn('flex flex-col', className)} {...props}>
        {children}
      </div>
    </TabsContext>
  );
}

/* ─── List ─── */

type TabsListProps = React.HTMLAttributes<HTMLDivElement>;

/** Horizontal tab button strip */
function TabsList({ className, children, ...props }: TabsListProps): React.JSX.Element {
  return (
    <div
      role="tablist"
      className={cn(
        'flex border-b border-border',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/* ─── Trigger ─── */

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

/** Individual tab button */
function TabsTrigger({ value, className, children, ...props }: TabsTriggerProps): React.JSX.Element {
  const { activeTab, onTabChange } = useTabsContext();
  const isActive = activeTab === value;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      data-state={isActive ? 'active' : 'inactive'}
      className={cn(
        'flex-1 px-3 py-2.5 text-xs font-medium transition-colors border-b-2 border-transparent',
        isActive
          ? 'text-primary border-b-primary'
          : 'text-muted-foreground hover:text-foreground',
        className,
      )}
      onClick={() => onTabChange(value)}
      {...props}
    >
      {children}
    </button>
  );
}

/* ─── Content ─── */

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

/** Tab panel — only renders when active */
function TabsContent({ value, className, children, ...props }: TabsContentProps): React.JSX.Element | null {
  const { activeTab } = useTabsContext();
  if (activeTab !== value) return null;

  return (
    <div
      role="tabpanel"
      className={cn('flex-1 overflow-hidden', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
